import type { CSSProperties, ReactNode } from 'react';

/**
 * HaiIcon — 全站圖示單一來源(R209;CIS「圖示單一來源」規範)。
 * -----------------------------------------------------------------------------
 * 62 顆 icon 逐顆對照 D3 鏡像 `icons.js`(`window.HAI_IC`)移植成 JSX:
 *   - `class="icon-stroke"` 展開為 `STROKE`(currentColor / 1.6 / round;素材庫.html:18)
 *   - 個別覆蓋 stroke-width 的(ck 2 / x 1.8 …)照原樣保留
 *   - `play` / `img` / `txt` / `playSm` 的硬編色是**媒體縮圖上的內容色**
 *     (白圈黑三角壓在任意底色縮圖上),theme-independent,依 lint 慣例 SVG attr 例外
 * 規範:禁 unicode 字元圖示(✓ ✕ ⊞ ▶ emoji);同一動作同一畫法;不同尺寸用
 * `size` prop 縮放,不新增定義(icons.js 頭註)。
 *
 * 用法:`<HaiIcon name="search" />`(原生尺寸)/ `<HaiIcon name="ck" size={14} />`。
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

interface IconDef {
  w: number;
  h: number;
  vb: string;
  node: ReactNode;
}

const ICONS = {
  play: {
    w: 26, h: 26, vb: '0 0 24 24',
    node: (
      <>
        <circle cx={12} cy={12} r={10} fill="rgba(255,255,255,.92)" />
        <path d="M10 8.5 L16 12 L10 15.5 Z" fill="#0F172A" />
      </>
    ),
  },
  img: {
    w: 20, h: 20, vb: '0 0 24 24',
    node: (
      <>
        <rect x={4} y={5} width={16} height={14} rx={2} fill="none" stroke="#fff" strokeWidth={1.7} />
        <circle cx={9} cy={10} r={1.7} fill="#fff" />
        <path d="M5 17 L10 12 L14 15.5 L16.5 13.5 L19 16" fill="none" stroke="#fff" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  txt: {
    w: 22, h: 22, vb: '0 0 24 24',
    node: <path d="M5 7 H19 M5 12 H19 M5 17 H13" fill="none" stroke="#64748B" strokeWidth={1.8} strokeLinecap="round" />,
  },
  ck: {
    w: 11, h: 11, vb: '0 0 12 12',
    node: <path d="M2.4 6.2 L4.8 8.6 L9.6 3.4" {...STROKE} strokeWidth={2} />,
  },
  ckLg: {
    w: 14, h: 14, vb: '0 0 12 12',
    node: <path d="M2.4 6.2 L4.8 8.6 L9.6 3.4" {...STROKE} strokeWidth={2.1} />,
  },
  minus: {
    w: 11, h: 11, vb: '0 0 12 12',
    node: <path d="M3 6 H9" {...STROKE} strokeWidth={2} />,
  },
  cv: {
    w: 13, h: 13, vb: '0 0 16 16',
    node: <path d="M4 6.5 L8 10 L12 6.5" {...STROKE} />,
  },
  more: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={4} cy={8} r={1.2} fill="currentColor" />
        <circle cx={8} cy={8} r={1.2} fill="currentColor" />
        <circle cx={12} cy={8} r={1.2} fill="currentColor" />
      </>
    ),
  },
  eye: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M1.6 8 C3.4 4.9 5.6 3.4 8 3.4 C10.4 3.4 12.6 4.9 14.4 8 C12.6 11.1 10.4 12.6 8 12.6 C5.6 12.6 3.4 11.1 1.6 8 Z" {...STROKE} />
        <circle cx={8} cy={8} r={2} {...STROKE} />
      </>
    ),
  },
  dl: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 2.6 V10 M5 7.2 L8 10.2 L11 7.2" {...STROKE} />
        <path d="M3 12.6 H13" {...STROKE} />
      </>
    ),
  },
  move: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M2 4.4 a1 1 0 0 1 1 -1 H6 L7.6 5.2 H13 a1 1 0 0 1 1 1 V11.6 a1 1 0 0 1 -1 1 H3 a1 1 0 0 1 -1 -1 Z" {...STROKE} />,
  },
  score: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M3 8.2 L6.2 11.4 L13 4.6" {...STROKE} />,
  },
  scan: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M2.4 5.4 V3.6 a1.2 1.2 0 0 1 1.2 -1.2 H5.4" {...STROKE} />
        <path d="M10.6 2.4 H12.4 a1.2 1.2 0 0 1 1.2 1.2 V5.4" {...STROKE} />
        <path d="M13.6 10.6 V12.4 a1.2 1.2 0 0 1 -1.2 1.2 H10.6" {...STROKE} />
        <path d="M5.4 13.6 H3.6 a1.2 1.2 0 0 1 -1.2 -1.2 V10.6" {...STROKE} />
        <path d="M5.2 8 L7.1 9.9 L10.8 6.2" {...STROKE} />
      </>
    ),
  },
  // pause / vList / plus:icons.js 原始碼 class="icon-stroke" 與 stroke-width attr 並存,
  // CSS class(1.6)特異性壓過 presentation attribute → 設計實渲染是 1.6(panel 抓漏)。
  pause: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M6.2 3.6 V12.4 M9.8 3.6 V12.4" {...STROKE} />,
  },
  resume: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M5.4 3.4 L12.2 8 L5.4 12.6 Z" {...STROKE} />,
  },
  trash: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M2.8 4.2 H13.2" {...STROKE} />
        <path d="M6.2 4.2 V2.8 H9.8 V4.2" {...STROKE} />
        <path d="M4 4.2 L4.7 13 H11.3 L12 4.2" {...STROKE} />
      </>
    ),
  },
  x: {
    w: 13, h: 13, vb: '0 0 14 14',
    node: <path d="M3.5 3.5 L10.5 10.5 M10.5 3.5 L3.5 10.5" {...STROKE} strokeWidth={1.8} />,
  },
  imgSm: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: (
      <>
        <rect x={2} y={3} width={12} height={10} rx={1} {...STROKE} />
        <circle cx={6} cy={7} r={1.2} {...STROKE} />
        <path d="M2 12 L7 8 L14 12" {...STROKE} />
      </>
    ),
  },
  vidSm: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: (
      <>
        <rect x={2} y={4} width={9} height={8} rx={1} {...STROKE} />
        <path d="M11 7 L14 5 V11 L11 9 Z" {...STROKE} />
      </>
    ),
  },
  txtSm: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: <path d="M3 4 H13 M3 8 H13 M3 12 H9" {...STROKE} />,
  },
  playSm: {
    w: 13, h: 13, vb: '0 0 14 14',
    node: <path d="M4.6 3 L11 7 L4.6 11 Z" fill="#0F172A" />,
  },
  spark: {
    w: 14, h: 14, vb: '0 0 14 14',
    node: <path d="M7 1l1.4 3.6L12 6 8.4 7.4 7 11 5.6 7.4 2 6l3.6-1.4z" fill="currentColor" />,
  },
  up: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 11 V3 M5 6 L8 3 L11 6" {...STROKE} />
        <path d="M3 11.5 V12.5 a1 1 0 0 0 1 1 H12 a1 1 0 0 0 1 -1 V11.5" {...STROKE} />
      </>
    ),
  },
  empty: {
    w: 26, h: 26, vb: '0 0 24 24',
    node: (
      <g fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 L21 7.6 L12 12.2 L3 7.6 Z" />
        <path d="M3.6 12 L12 16.3 L20.4 12" />
        <path d="M3.6 16 L12 20.3 L20.4 16" />
      </g>
    ),
  },
  card: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 2 L14 5 L8 8 L2 5 Z" {...STROKE} />
        <path d="M2.5 8 L8 10.8 L13.5 8" {...STROKE} />
        <path d="M2.5 10.8 L8 13.6 L13.5 10.8" {...STROKE} />
      </>
    ),
  },
  dlr: {
    w: 16, h: 16, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 2.6 V10 M5 7.2 L8 10.2 L11 7.2" {...STROKE} />
        <path d="M3 12.6 H13" {...STROKE} />
      </>
    ),
  },
  sortUp: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: <path d="M8 13 L8 3 M4.5 6.5 L8 3 L11.5 6.5" {...STROKE} />,
  },
  sortDown: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: <path d="M8 3 L8 13 M4.5 9.5 L8 13 L11.5 9.5" {...STROKE} />,
  },
  search: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={7} cy={7} r={4.6} {...STROKE} />
        <path d="M10.4 10.4 L13.5 13.5" {...STROKE} />
      </>
    ),
  },
  vList: {
    w: 14, h: 14, vb: '0 0 14 14',
    node: <path d="M2 3.5 H12 M2 7 H12 M2 10.5 H12" {...STROKE} />,
  },
  vGrid: {
    w: 14, h: 14, vb: '0 0 14 14',
    node: (
      <>
        <rect x={2} y={2} width={4.2} height={4.2} rx={1} {...STROKE} />
        <rect x={7.8} y={2} width={4.2} height={4.2} rx={1} {...STROKE} />
        <rect x={2} y={7.8} width={4.2} height={4.2} rx={1} {...STROKE} />
        <rect x={7.8} y={7.8} width={4.2} height={4.2} rx={1} {...STROKE} />
      </>
    ),
  },
  pgPrev: {
    w: 13, h: 13, vb: '0 0 16 16',
    node: <path d="M10 3.5 L6 8 L10 12.5" {...STROKE} />,
  },
  pgNext: {
    w: 13, h: 13, vb: '0 0 16 16',
    node: <path d="M6 3.5 L10 8 L6 12.5" {...STROKE} />,
  },
  info: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={8} cy={8} r={6.2} fill="none" stroke="currentColor" strokeWidth={1.3} />
        <path d="M8 7 V11" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" />
        <circle cx={8} cy={4.9} r={0.8} fill="currentColor" />
      </>
    ),
  },
  warn: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 2.4 L14.2 13 H1.8 Z" {...STROKE} />
        <path d="M8 6.6 V9.2" {...STROKE} />
        <circle cx={8} cy={11.1} r={0.85} fill="currentColor" />
      </>
    ),
  },
  rpt: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M4 2.6 H10 L12.6 5.2 V13.4 H4 Z" {...STROKE} />
        <path d="M9.6 2.6 V5.4 H12.6" {...STROKE} />
        <path d="M6 8.6 H10.4 M6 10.8 H9" {...STROKE} />
      </>
    ),
  },
  redo: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M13 8 a5 5 0 1 1 -1.6 -3.7" {...STROKE} />
        <path d="M13 2.6 V5.2 H10.4" {...STROKE} />
      </>
    ),
  },
  // R393 — D3 icons.js `undo`(redo 的鏡像;剪輯頁「復原」原本自己畫一顆)
  undo: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M3 8 a5 5 0 1 0 1.6 -3.7" {...STROKE} />
        <path d="M3 2.6 V5.2 H5.6" {...STROKE} />
      </>
    ),
  },
  edit: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M11.2 2.9 L13.1 4.8 L5.6 12.3 L3 13 L3.7 10.4 Z" {...STROKE} />,
  },
  copy: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: (
      <>
        <rect x={5.4} y={5.4} width={8.2} height={8.2} rx={1.6} {...STROKE} />
        <path d="M10.6 3.4 a1.6 1.6 0 0 0 -1.6 -1.6 H4 a1.6 1.6 0 0 0 -1.6 1.6 V9.6 a1.6 1.6 0 0 0 1.6 1.6" {...STROKE} />
      </>
    ),
  },
  min: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <path d="M3.5 12.5 H12.5" {...STROKE} />
        <path d="M8 3 V9 M5.2 6.4 L8 9.2 L10.8 6.4" {...STROKE} />
      </>
    ),
  },
  list: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: <path d="M3 4.2 H13 M3 8 H13 M3 11.8 H9" {...STROKE} />,
  },
  bulb: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <path d="M6 11.4 a4 4 0 1 1 4 0 V13 H6 Z" {...STROKE} />
        <path d="M6.6 14.2 H9.4" {...STROKE} />
      </>
    ),
  },
  flag: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: <path d="M4 13.5 V2.6 h7.6 l-1.5 2.6 1.5 2.6 H4" {...STROKE} />,
  },
  grid: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <rect x={2.4} y={2.4} width={5} height={5} rx={1.2} {...STROKE} />
        <rect x={8.6} y={2.4} width={5} height={5} rx={1.2} {...STROKE} />
        <rect x={2.4} y={8.6} width={5} height={5} rx={1.2} {...STROKE} />
        <rect x={8.6} y={8.6} width={5} height={5} rx={1.2} {...STROKE} />
      </>
    ),
  },
  clock: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={8} cy={8} r={6} {...STROKE} />
        <path d="M8 4.6 V8.2 L10.4 9.6" {...STROKE} />
      </>
    ),
  },
  gear: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={8} cy={8} r={2.2} {...STROKE} />
        <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4" {...STROKE} />
      </>
    ),
  },
  plus: {
    w: 12, h: 12, vb: '0 0 12 12',
    node: <path d="M6 2v8M2 6h8" {...STROKE} />,
  },
  user: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={8} cy={6} r={3} {...STROKE} />
        <path d="M2 14 a6 6 0 0 1 12 0" {...STROKE} />
      </>
    ),
  },
  logout: {
    w: 16, h: 16, vb: '0 0 16 16',
    node: (
      <>
        <path d="M6.5 2.5 H4 a1.2 1.2 0 0 0 -1.2 1.2 V12.3 a1.2 1.2 0 0 0 1.2 1.2 H6.5" {...STROKE} />
        <path d="M10 5 L13 8 L10 11 M13 8 H6.5" {...STROKE} />
      </>
    ),
  },
  star: {
    w: 16, h: 16, vb: '0 0 16 16',
    node: <path d="M8 2 L9 4 L11 3 L11 5 L13 6 L11 7 L11 9 L9 10 L8 12 L7 10 L5 9 L5 7 L3 6 L5 5 L5 3 L7 4 Z" {...STROKE} />,
  },
  swap: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M4 6.5 L8 10 L12 6.5" {...STROKE} />,
  },
  unfold: {
    w: 15, h: 15, vb: '0 0 16 16',
    node: <path d="M5 6.5 L8 3.5 L11 6.5 M5 9.5 L8 12.5 L11 9.5" {...STROKE} />,
  },
  panelClose: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <rect x={2.5} y={3} width={11} height={10} rx={2.2} {...STROKE} />
        <path d="M6.4 3 V13" {...STROKE} />
      </>
    ),
  },
  panelOpen: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <rect x={2.5} y={3} width={11} height={10} rx={2.2} {...STROKE} />
        <path d="M9.6 3 V13" {...STROKE} />
        <path d="M5 6.2 L7 8 L5 9.8" {...STROKE} />
      </>
    ),
  },
  home: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: <path d="M2 7 L8 2 L14 7 L14 13 H10 V9 H6 V13 H2 Z" {...STROKE} />,
  },
  folder: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: <path d="M2 4 a1 1 0 0 1 1 -1 H6 L8 5 H13 a1 1 0 0 1 1 1 V12 a1 1 0 0 1 -1 1 H3 a1 1 0 0 1 -1 -1 Z" {...STROKE} />,
  },
  lib: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 2 L14 5.2 L8 8.4 L2 5.2 Z" {...STROKE} />
        <path d="M2.4 8.2 L8 11 L13.6 8.2" {...STROKE} />
        <path d="M2.4 10.8 L8 13.6 L13.6 10.8" {...STROKE} />
      </>
    ),
  },
  chart: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: <path d="M2.5 13 V9 M6 13 V4 M9.5 13 V7 M13 13 V2.5" {...STROKE} />,
  },
  shield: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <path d="M8 2.2 L13 4.2 V8 c0 3.2 -2.4 5.1 -5 5.9 C5.4 13.1 3 11.2 3 8 V4.2 Z" {...STROKE} />
        <path d="M6 7.9 L7.4 9.3 L10 6.4" {...STROKE} />
      </>
    ),
  },
  link: {
    w: 17, h: 17, vb: '0 0 16 16',
    node: (
      <>
        <path d="M6.4 9.6 L9.6 6.4" {...STROKE} />
        <path d="M8.4 4.6 L9.9 3.1 a2.4 2.4 0 0 1 3.4 3.4 L11.6 8" {...STROKE} />
        <path d="M7.6 11.4 L6.1 12.9 a2.4 2.4 0 0 1 -3.4 -3.4 L4.4 8" {...STROKE} />
      </>
    ),
  },
  // R393 — D3 icons.js `grip`(拖曳握把;指標排序抽屜原本自己畫一顆)
  grip: {
    w: 14, h: 14, vb: '0 0 16 16',
    node: (
      <>
        <circle cx={6} cy={4} r={1.2} fill="currentColor" />
        <circle cx={10} cy={4} r={1.2} fill="currentColor" />
        <circle cx={6} cy={8} r={1.2} fill="currentColor" />
        <circle cx={10} cy={8} r={1.2} fill="currentColor" />
        <circle cx={6} cy={12} r={1.2} fill="currentColor" />
        <circle cx={10} cy={12} r={1.2} fill="currentColor" />
      </>
    ),
  },
} satisfies Record<string, IconDef>;

export type IconName = keyof typeof ICONS;

/**
 * 全部 icon 名(**從 ICONS 本體導出**,不是手抄清單)。
 * 測試用它跑 render-all,新增第 61 顆時「恰 60 顆」那條會紅 — 手抄清單擋不到新增。
 */
export const ICON_NAMES = Object.keys(ICONS) as IconName[];

export interface HaiIconProps {
  name: IconName;
  /** 覆蓋原生尺寸(等比;icons.js 慣例「不同尺寸用縮放,不新增定義」)。 */
  size?: number;
  style?: CSSProperties;
  className?: string;
}

export function HaiIcon({ name, size, style, className }: HaiIconProps) {
  const def = ICONS[name];
  const w = size ?? def.w;
  const h = size !== undefined ? Math.round(size * (def.h / def.w)) : def.h;
  return (
    <svg
      width={w}
      height={h}
      viewBox={def.vb}
      aria-hidden
      focusable={false}
      style={style}
      className={className}
      data-icon={name}
    >
      {def.node}
    </svg>
  );
}
